<script setup lang="ts">
import CloseCircleIcon from '@/assets/icons/shared/close-circle.svg';
import EditNameMediumIcon from '@/assets/icons/shared/edit-name-medium.svg';
import type { IconComponent } from '@/modules/common/types/icon.type.ts';
import type { TooltipLine } from '@/modules/common/types/ui.ts';
import { type Component, computed, ref, useTemplateRef } from 'vue';
import UiTooltip from '@/modules/ui/components/ui/UiTooltip.vue';

export type CardItem = {
  id?: string | number;
  href?: string;
  tooltip?: string;
  tooltipLines?: TooltipLine[];
  subtitle?: string;
  modified?: boolean;
  [key: string]: unknown;
};

const {
  tag = 'div',
  titleKey = 'title',
  item,
  editable,
  active,
  iconSpaced = false,
  closeOnHover = false,
  tooltipOnCard = false,
} = defineProps<{
  index?: number;
  tag?: 'div' | 'li';
  icon: IconComponent;
  item: CardItem;
  readonly?: boolean;
  editable?: boolean;
  active?: boolean;
  footerComponent?: Component;
  titleKey?: string;
  iconSpaced?: boolean;
  closeOnHover?: boolean;
  tooltipOnCard?: boolean;
}>();

const emit = defineEmits<{
  change: [value: Partial<CardItem>];
  remove: [];
  edit: [anchor?: HTMLElement];
}>();

const titleRef = useTemplateRef('title');

const showTooltip = ref(false);

const title = computed(() => String(item[titleKey] ?? ''));

const cardClass = computed(() => ({
  'ui-card': true,
  '_icon-spaced': iconSpaced,
  '_close-on-hover': closeOnHover,
  '_editable': editable,
  '_active': active,
}));

const headerTag = computed(() => (item.href ? 'a' : 'span'));
const headerLinkClass = computed(() => ({ _blue: item.href, _hover: item.href }));
const tooltipText = computed(() => (item.tooltipLines ? undefined : item.tooltip || title.value));
const tooltipAlign = computed(() => (item.tooltip || item.tooltipLines ? 'left' : 'center'));

const cardRootBind = computed(() =>
  tooltipOnCard
    ? {
        is: tag,
        text: tooltipText.value,
        align: tooltipAlign.value,
        disabled: !showTooltip.value,
      }
    : {}
);

const headerBind = computed(() => {
  const link = item.href ? { href: item.href, target: '_blank' } : {};

  if (tooltipOnCard) {
    return link;
  }

  return {
    is: headerTag.value,
    text: tooltipText.value,
    align: tooltipAlign.value,
    disabled: !showTooltip.value,
    ...link,
  };
});

function setShowTooltip() {
  showTooltip.value =
    !!(item.tooltip || item.tooltipLines) ||
    (!!titleRef.value && titleRef.value.scrollHeight > titleRef.value.clientHeight + 1);
}

function onFooterChange(changes: Partial<CardItem>) {
  emit('change', changes);
}
</script>

<template>
  <component
    :is="tooltipOnCard ? UiTooltip : tag"
    :class="cardClass"
    v-bind="cardRootBind"
    @mouseenter="tooltipOnCard && setShowTooltip()"
  >
    <template
      v-if="tooltipOnCard && item.tooltipLines"
      #tooltip
    >
      <p
        v-for="line in item.tooltipLines"
        :key="`${line.label ?? ''}:${line.value}`"
        class="tooltip-line"
      >
        {{ line.label ? `${$maybeT(line.label)}: ` : '' }}<b>{{ line.value }}</b>
      </p>
    </template>

    <button
      v-show="!readonly"
      type="button"
      class="close"
      tabindex="-1"
      @mousedown.stop.prevent
      @click.stop="$emit('remove')"
    >
      <CloseCircleIcon class="close-icon" />
    </button>

    <component
      :is="tooltipOnCard ? headerTag : UiTooltip"
      class="header"
      :class="headerLinkClass"
      v-bind="headerBind"
      @mouseenter="!tooltipOnCard && setShowTooltip()"
    >
      <template
        v-if="!tooltipOnCard && item.tooltipLines"
        #tooltip
      >
        <p
          v-for="line in item.tooltipLines"
          :key="`${line.label ?? ''}:${line.value}`"
          class="tooltip-line"
        >
          {{ line.label ? `${$maybeT(line.label)}: ` : '' }}<b>{{ line.value }}</b>
        </p>
      </template>

      <b
        ref="title"
        class="title"
        :class="{ _modified: item.modified }"
        ><i
          class="dot"
          aria-hidden="true"
        />{{ title }}</b
      >
      <span
        v-if="item.subtitle"
        class="subtitle"
        >{{ item.subtitle }}</span
      >
    </component>

    <component
      :is="icon"
      class="icon"
      width="60px"
      height="60px"
    />

    <component
      :is="footerComponent"
      v-bind="item"
      :index="index"
      @change="onFooterChange"
    />

    <button
      v-if="editable"
      type="button"
      class="edit"
      @click.stop="$emit('edit', $event.currentTarget as HTMLElement)"
    >
      <EditNameMediumIcon />
    </button>
  </component>
</template>

<style scoped lang="scss">
@mixin card-collapsed {
  :deep(.upload-revision-badge) {
    gap: 0;
    background-color: var(--color-gray-500);
  }

  :deep(.upload-revision-badge .badge-label) {
    grid-template-columns: 0fr;
  }

  :deep(.upload-revision-badge .badge-icon) {
    opacity: 0;
  }

  :deep(.upload-revision-badge),
  :deep(.upload-revision-badge .badge-label),
  :deep(.upload-revision-badge .badge-icon) {
    transition-delay: 0s;
  }

  .edit {
    opacity: 1;
    transition:
      color var(--transition-fast),
      opacity var(--transition-fast);
  }
}

.ui-card {
  display: flex;
  padding: 4px;
  overflow: hidden;
  flex-direction: column;
  color: var(--color-gray-600);
  border-radius: 8px;
  background-color: var(--color-gray-075);

  &._icon-spaced {
    .icon {
      margin-top: 16px;
      margin-bottom: 16px;

      &:last-child {
        margin-bottom: 16px;
      }
    }
  }

  &._active {
    @include card-collapsed;

    .edit {
      color: var(--color-gray-700);
    }
  }
}

.edit {
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: flex;
  color: var(--color-gray-500);
  opacity: 0;
  transition:
    color var(--transition-fast),
    opacity var(--transition-fast) var(--duration-fast);

  @media (hover: hover) {
    &:hover {
      color: var(--color-gray-700);
    }
  }
}

:deep(.upload-revision-badge),
:deep(.upload-revision-badge .badge-label),
:deep(.upload-revision-badge .badge-icon) {
  transition-duration: var(--duration-fast);
  transition-timing-function: ease-in-out;
  transition-delay: var(--duration-fast);
}

:deep(.upload-revision-badge) {
  transition-property: background-color, gap;
}

:deep(.upload-revision-badge .badge-label) {
  transition-property: grid-template-columns;
}

:deep(.upload-revision-badge .badge-icon) {
  transition-property: opacity;
}

@media (hover: hover) {
  .ui-card._editable:hover {
    @include card-collapsed;
  }
}

.header {
  flex-grow: 1;
  margin-bottom: 4px;
  padding: 4px;
  min-height: 40px;
}

.title {
  position: relative;
  display: -webkit-box;
  max-height: 32px;
  overflow: hidden;
  text-overflow: ellipsis;
  word-wrap: break-word;
  white-space: pre-line;
  font-weight: 700;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;

  &:before {
    content: '';
    padding-left: 0;
    transition: padding-left var(--transition);
  }

  &._modified:before {
    padding-left: 12px;
  }
}

.dot {
  position: absolute;
  top: 4px;
  left: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--color-blue-700);
  opacity: 0;
  transition: opacity var(--transition);

  ._modified & {
    opacity: 1;
  }
}

.subtitle {
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  word-wrap: break-word;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.tooltip-line {
  font-size: var(--font-size);
  line-height: var(--line-height);
  word-break: break-word;

  b {
    font-weight: var(--font-weight-extra-bold);
  }
}

.close {
  position: absolute;
  top: -6px;
  right: -6px;
  display: flex;
  width: 18px;
  height: 18px;
  color: var(--color-gray-500);
  cursor: pointer;
  transition: color var(--transition-fast);

  @media (hover: hover) {
    &:hover {
      color: var(--color-gray-700);
    }
  }
}

.ui-card._editable:not(._close-on-hover) {
  @media (hover: hover) {
    &:hover .close {
      opacity: 1;
      transition:
        color var(--transition-fast),
        opacity var(--transition-fast);
    }

    .close {
      opacity: 0;
      transition:
        color var(--transition-fast),
        opacity var(--transition-fast) var(--duration-fast);
    }
  }
}

.ui-card._close-on-hover {
  @media (hover: hover) {
    &:hover,
    &:focus-within {
      .close {
        opacity: 1;
        pointer-events: auto;
        transition:
          color var(--transition-fast),
          opacity var(--transition-fast);
      }
    }

    .close {
      opacity: 0;
      pointer-events: none;
      transition:
        color var(--transition-fast),
        opacity var(--transition-fast) var(--duration-fast);
    }
  }

  @media (hover: none) {
    .close {
      opacity: 1;
      pointer-events: auto;
    }
  }
}

.close-icon {
  width: 100%;
  height: 100%;
}

.icon {
  fill: var(--color-gray-200);
  align-self: center;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 32px;
  }
}
</style>
